import { Component, DestroyRef, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { KPI } from '../../../@core/api';
import { NotationsService } from '../../../@core/utils';

import { StateLevelsService } from './../../../@core/utils/state-levels.service';
import { NbTooltipModule } from '@nebular/theme';
import { NgClass, DecimalPipe } from '@angular/common';

@Component({
    selector: 'app-displayed-note',
    styleUrls: ['./displayed-note.component.scss'],
    templateUrl: './displayed-note.component.html',
    standalone: true,
    imports: [
    NbTooltipModule,
    NgClass,
    DecimalPipe,
    TranslateModule
],
})
export class DisplayedNoteComponent implements OnChanges, OnInit {

  @Input() public note: number = null;
  @Input() public isNextAssetNote: boolean = false;
  @Input() public nbAssets: any;
  @Input() public isCurrent: boolean;
  @Input() public startYear: number;
  @Input() public nbNotations: any;
  @Input() public notationMissingReason: any;
  @Input() public asset: KPI.Asset;
  @Input() public lastNotationYear: number = null;

  public tooltip: string;
  public noteTooltip: string;
  public missingReasonLabel: string = '';
  private levelsMap: { [key: string]: { name: string } } = {};
  public noteLabel: string = '';

  private readonly currentYear = new Date().getFullYear();
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private translate: TranslateService,
    private stateLevelsService: StateLevelsService,
    private notationsService: NotationsService,
  ) { }

  public ngOnInit(): void {
    this.stateLevelsService.levelsNames$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((levelsNames) => {
      this.levelsMap = {
        25: { name: levelsNames[0] },
        50: { name: levelsNames[1] },
        75: { name: levelsNames[2] },
        100: { name: levelsNames[3] },
      };
      this.updateNoteTooltip();
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.note === null && this.asset && typeof this.asset.notation_missing_reason !== 'undefined') {
      const translationPath = this.notationsService.getMissingNotationReasonLabel(this.asset.notation_missing_reason);
      this.translate.get(translationPath).subscribe(translation => {
        this.missingReasonLabel = translation;
      });
    } else if (this.note !== undefined && this.note !== null) {
      if (this.isNextAssetNote) {
        this.noteLabel = this.translate.instant('New equipment')
          + ' '
          + this.translate.instant('in')
          + ' '
          + this.stateLevelsService.getTechnicalStateLabelFromNote(this.note).toLowerCase();
      } else if (this.startYear !== null && this.startYear !== undefined) {
        this.noteLabel = this.startYear > this.currentYear
          ? this.translate.instant('Projection')
          : this.translate.instant('Technical state');
      } else {
        this.noteLabel = this.lastNotationYear
          ? this.stateLevelsService.getTechnicalStateLabelFromNote(this.note).toLowerCase()
          : this.stateLevelsService.getTechnicalStateLabelFromNote(this.note);
      }
    }

    this.updateNoteTooltip();

    if (changes.nbAssets && this.nbAssets) {
      // For every other notation type than asset-notation
      if (Number.isInteger(this.nbAssets.notations) && Number.isInteger(this.nbAssets.computed)) {

        if (!this.isCurrent) {
          // If future notation take only computed notations
          this.translate
            .get('theme.displayed-note.tooltip_future', {
              computed: this.nbNotations ? this.nbNotations.computed : this.nbAssets.computed,
            })
            .subscribe((tooltip) => (this.tooltip = tooltip));
        } else {
          // Display tooltip for asset
          this.translate
            .get('theme.displayed-note.tooltip_assets', {
              nbAssets: this.nbAssets.notations,
              startYear: this.startYear,
              computed: this.nbAssets.computed,
            })
            .subscribe((tooltip) => (this.tooltip = tooltip));
        }
      }
    }
    if (changes.nbNotations && this.nbNotations &&
      Number.isInteger(this.nbNotations.notations) &&
      Number.isInteger(this.nbNotations.computed)) {

      if (!this.isCurrent) {
        // If future notation take only computed notations
        this.translate
          .get('theme.displayed-note.tooltip_future', {
            computed: this.nbNotations ? this.nbNotations.computed : this.nbAssets.computed,
          })
          .subscribe((tooltip) => (this.tooltip = tooltip));
      }
    }
  }

  private updateNoteTooltip(): void {
    this.noteTooltip = this.translate.instant(
      'pages.common.charts.technical-state-barchart.technical_state_current',
      { year: '' },
    );

    if (this.note === null || this.note === undefined) {
      return;
    }

    const state = Object.keys(this.levelsMap).sort((a, b) => +a - +b).find(key => this.note <= +key);
    if (state && this.levelsMap[state]) {
      this.noteTooltip += ` : ${this.levelsMap[state].name}`;
    }
  }
}
